import pc from "picocolors";
import { formatWakeLoopCliCommand } from "../runtime/cli-entry-command";
import type { createPresenter } from "../runtime/presenter/index";
import type { LegacyWakeLoopSkillFinding } from "../skills/legacy-audit";
import type { InfoCardRow } from "../ui/info-card";
import { outLine } from "../ui/io";
import { formatInlineCommandList, renderSkillsTextCard } from "./skills-format";

export function emitLegacyWakeLoopSkillFindingsHumanCard(input: {
  legacyFindings: LegacyWakeLoopSkillFinding[];
}): void {
  if (input.legacyFindings.length === 0) {
    return;
  }
  outLine("");
  renderSkillsTextCard({
    title: `Legacy WakeLoop Skill Copies (${input.legacyFindings.length})`,
    rows: input.legacyFindings.map((finding) => ({
      label: finding.skillId,
      value: [
        `path: ${finding.skillDir}`,
        "not managed by this WakeLoop installation",
        finding.problem,
        finding.recommendation,
        `cleanup: ${finding.cleanupCommands.join(" or ")}`,
      ].join(" · "),
    })),
    compact: true,
  });
}

export function buildLegacyWakeLoopSkillNextStepRows(
  legacyFindings: LegacyWakeLoopSkillFinding[]
): InfoCardRow[] {
  if (legacyFindings.length === 0) {
    return [];
  }
  const installCommands = buildLegacyWakeLoopSkillInstallCommands(
    legacyFindings
  ).map((command) => formatWakeLoopCliCommand(command));
  const verificationCommand = formatWakeLoopCliCommand(
    legacyFindings[0]?.verificationCommand ??
      "wakeloop skills check --view agent"
  );
  return [
    {
      label: "legacy WakeLoop skills",
      value: pc.bold(
        [
          "Do not read the listed legacy WakeLoop skill instructions.",
          `Run ${formatInlineCommandList(installCommands)} for each affected skill root.`,
          "After the install succeeds, rename or remove the legacy directories.",
          `Then rerun \`${verificationCommand}\`.`,
        ].join(" ")
      ),
    },
  ];
}

export function formatLegacyWakeLoopSkillFindingsForAgentView(
  legacyFindings: LegacyWakeLoopSkillFinding[]
): LegacyWakeLoopSkillFinding[] {
  return legacyFindings.map((finding) => ({
    ...finding,
    currentInstallCommand: formatWakeLoopCliCommand(
      finding.currentInstallCommand
    ),
    verificationCommand: formatWakeLoopCliCommand(finding.verificationCommand),
  }));
}

export function buildLegacyWakeLoopSkillAgentReview(
  legacyFindings: LegacyWakeLoopSkillFinding[]
): {
  priority: "high";
  reason: string;
  nextStep: string;
  nextActions: string[];
  installCommands: string[];
  paths: string[];
  cleanupCommands: string[];
  verificationCommand: string;
} {
  const installCommands =
    buildLegacyWakeLoopSkillInstallCommands(legacyFindings);
  const verificationCommand =
    legacyFindings[0]?.verificationCommand ??
    "wakeloop skills check --view agent";
  return {
    priority: "high",
    reason:
      "Legacy or unmanaged WakeLoop skill copies can make agents read old WakeLoop instructions even when the current WakeLoop-managed skill is up to date.",
    nextStep:
      "Do not read or follow the listed legacy WakeLoop skill instructions. Run each install command, then remove or rename each listed legacy directory, then rerun the WakeLoop skills check.",
    nextActions: [
      "Do not read or follow instructions from the listed legacy WakeLoop skill directories.",
      ...installCommands.map((command) => `Run ${command}.`),
      "After the current WakeLoop skill install succeeds for that root, rename or remove each listed legacy directory. Prefer mv ... .legacy when you need reversible cleanup.",
      `Rerun ${verificationCommand}.`,
    ],
    installCommands,
    paths: legacyFindings.map((finding) => finding.skillDir),
    cleanupCommands: legacyFindings.flatMap(
      (finding) => finding.cleanupCommands
    ),
    verificationCommand,
  };
}

function buildLegacyWakeLoopSkillInstallCommands(
  legacyFindings: LegacyWakeLoopSkillFinding[]
): string[] {
  return [
    ...new Set(legacyFindings.map((finding) => finding.currentInstallCommand)),
  ].sort((left, right) => left.localeCompare(right));
}

export function emitLegacyWakeLoopSkillAgentAttention(input: {
  legacyFindings: LegacyWakeLoopSkillFinding[];
  presenter: ReturnType<typeof createPresenter>;
  source: "skills.check" | "skills.install";
}): void {
  if (input.legacyFindings.length === 0) {
    return;
  }

  const review = buildLegacyWakeLoopSkillAgentReview(input.legacyFindings);
  const directoryLabel =
    input.legacyFindings.length === 1 ? "directory" : "directories";
  const installCommands = review.installCommands.map((command) =>
    formatWakeLoopCliCommand(command)
  );
  const verificationCommand = formatWakeLoopCliCommand(
    review.verificationCommand
  );
  input.presenter.line({
    code: `${input.source}.legacy_attention_required`,
    text: [
      `attention required: found ${input.legacyFindings.length} legacy or unmanaged WakeLoop skill ${directoryLabel}: ${review.paths.join(", ")}.`,
      "These copies are outside the current WakeLoop-managed skill install and can make agents read old WakeLoop instructions.",
      "Do not read or follow those skill instructions.",
      `Next: run ${formatInlineCommandList(installCommands)}.`,
      "After the current skill is installed, rename or remove each listed directory; WakeLoop will not do this automatically.",
      `Then rerun \`${verificationCommand}\`.`,
    ].join(" "),
    data: {
      legacyFindingCount: input.legacyFindings.length,
      legacyFindings: input.legacyFindings,
      legacyReview: review,
    },
  });
}
